import React, { useRef, useState, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faChevronLeft, faChevronRight, faSearchPlus, faSearchMinus, faPrint, faTimes, faExpand, faCompress, faBars } from '@fortawesome/free-solid-svg-icons';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './PdfViewer.scss';

pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.js?v=${pdfjs.version}`;

// pdf.js 3.x no longer bundles fallbacks for non-embedded fonts;
// without these, PDFs using standard/CID fonts render garbled glyphs
const PDF_OPTIONS = {
  cMapUrl: '/cmaps/',
  cMapPacked: true,
  standardFontDataUrl: '/standard_fonts/',
};

const getIsMobile = () => window.innerWidth <= 768;

const ThumbnailItem = React.memo(({
  pageNumber,
  isActive,
  pdfUrl,
  onPageClick
}) => {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <div
      className={`pdf-thumbnail ${isActive ? 'active' : ''}`}
      onClick={() => onPageClick(pageNumber)}
    >
      <div className="pdf-thumbnail-wrapper">
        {isLoading && <div className="pdf-thumbnail-skeleton"></div>}
        <Document
          file={pdfUrl}
          options={PDF_OPTIONS}
          onLoadSuccess={() => setIsLoading(false)}
          loading={<div className="pdf-thumbnail-skeleton"></div>}
        >
          <Page
            pageNumber={pageNumber}
            width={150}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </Document>
      </div>
      <div className="pdf-thumbnail-label">Page {pageNumber}</div>
    </div>
  );
});

ThumbnailItem.displayName = 'ThumbnailItem';

const ZOOM_LEVELS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3];
const DEFAULT_ZOOM = 1.25; 

const PdfViewer = ({ pdfToDisplay, pdfContainer }) => {
  const pdfContentRef = useRef();
  const documentContainerRef = useRef();
  const thumbnailCacheRef = useRef({});

  const [isMobile, setIsMobile] = useState(getIsMobile);

  // View state
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(isMobile ? 0.75 : DEFAULT_ZOOM);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSidebar, setShowSidebar] = useState(!isMobile);
  const [thumbnails, setThumbnails] = useState([]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = getIsMobile();
      setIsMobile(mobile);
      if (mobile) {
        setShowSidebar(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);

    const cachedThumbnails = thumbnailCacheRef.current[pdfToDisplay.url];
    if (cachedThumbnails && cachedThumbnails.length === numPages) {
      setThumbnails(cachedThumbnails);
    } else {
      const newThumbnails = Array.from({ length: numPages }, (_, i) => i + 1);
      thumbnailCacheRef.current[pdfToDisplay.url] = newThumbnails;
      setThumbnails(newThumbnails);
    }
  };

  const closePdfViewer = () => {
    setTimeout(() => {
      const mobile = getIsMobile();
      setNumPages(null);
      setPageNumber(1);
      setScale(mobile ? 0.75 : DEFAULT_ZOOM);
      setShowSidebar(!mobile);
      setIsFullscreen(false);
    }, 150);
    pdfContainer.current.classList.toggle('open');
  };

  const handlePrint = () => {
    if (pdfToDisplay) {
      const printWindow = window.open(pdfToDisplay.url);
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
      }
    }
  };

  const handleZoomIn = () => {
    setScale((prevScale) => {
      const currentIndex = ZOOM_LEVELS.findIndex(level => Math.abs(level - prevScale) < 0.01);
      const nextIndex = Math.min(currentIndex + 1, ZOOM_LEVELS.length - 1);
      return ZOOM_LEVELS[nextIndex];
    });
  };

  const handleZoomOut = () => {
    setScale((prevScale) => {
      const currentIndex = ZOOM_LEVELS.findIndex(level => Math.abs(level - prevScale) < 0.01);
      const prevIndex = Math.max(currentIndex - 1, 0);
      return ZOOM_LEVELS[prevIndex];
    });
  };

  const resetZoom = () => {
    setScale(DEFAULT_ZOOM);
  };

  const fitToWidth = () => {
    setScale(1.5);
  };

  const goToPrevPage = () => {
    setPageNumber((prevPage) => Math.max(prevPage - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber((prevPage) => Math.min(prevPage + 1, numPages || 1));
  };

  const goToPage = useCallback((page) => {
    setPageNumber(page);
  }, []);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  const toggleSidebar = useCallback(() => {
    setShowSidebar(prev => !prev);
  }, []);

  if (!pdfToDisplay) {
    return null;
  }

  return (
    <div className={`pdf-viewer ${isFullscreen ? 'fullscreen' : ''}`} onClick={closePdfViewer}>
      <div className="pdf-content native-viewer" ref={pdfContentRef} onClick={(e) => e.stopPropagation()}>
        <div className="pdf-top-toolbar">
          <div className="pdf-toolbar-left">
            <button className="pdf-icon-btn" onClick={toggleSidebar} title="Toggle Sidebar">
              <FontAwesomeIcon icon={faBars} />
            </button>
            <span className="pdf-filename">{pdfToDisplay.filename}</span>
          </div>

          <div className="pdf-toolbar-center">
            <button className="pdf-icon-btn" onClick={goToPrevPage} disabled={pageNumber <= 1}>
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>

            <div className="pdf-page-input-group">
              <input
                type="number"
                className="pdf-page-input"
                value={pageNumber}
                onChange={(e) => {
                  const page = parseInt(e.target.value);
                  if (page >= 1 && page <= numPages) {
                    goToPage(page);
                  }
                }}
                min="1"
                max={numPages || 1}
              />
              <span className="pdf-page-total">/ {numPages || '...'}</span>
            </div>

            <button className="pdf-icon-btn" onClick={goToNextPage} disabled={pageNumber >= (numPages || 1)}>
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>

          <div className="pdf-toolbar-right">
            <button className="pdf-icon-btn" onClick={handlePrint} title="Print">
              <FontAwesomeIcon icon={faPrint} />
            </button>
            <a
              href={pdfToDisplay.url}
              download={pdfToDisplay.filename}
              className="pdf-icon-btn"
              title="Download"
              onClick={(e) => e.stopPropagation()}
            >
              <FontAwesomeIcon icon={faDownload} />
            </a>
            <button className="pdf-icon-btn" onClick={toggleFullscreen} title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
              <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
            </button>
            <button className="pdf-icon-btn pdf-close-btn" onClick={closePdfViewer} title="Close">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        <div className="pdf-main-container">
          <div className={`pdf-sidebar ${!showSidebar ? 'hidden' : ''}`}>
            <div className="pdf-thumbnails">
              {thumbnails.map((thumb) => (
                <ThumbnailItem
                  key={thumb}
                  pageNumber={thumb}
                  isActive={pageNumber === thumb}
                  pdfUrl={pdfToDisplay.url}
                  onPageClick={goToPage}
                />
              ))}
            </div>
          </div>

          <div className="pdf-viewer-area">
            <div className="pdf-secondary-toolbar">
              <div className="pdf-zoom-controls">
                <button className="pdf-icon-btn" onClick={handleZoomOut} disabled={scale <= ZOOM_LEVELS[0]} title="Zoom Out">
                  <FontAwesomeIcon icon={faSearchMinus} />
                </button>
                <select
                  className="pdf-zoom-select"
                  value={Math.round(scale * 100)}
                  onChange={(e) => {
                    const newScale = parseInt(e.target.value) / 100;
                    if (ZOOM_LEVELS.includes(newScale)) {
                      setScale(newScale);
                    }
                  }}
                >
                  <option value="50">50%</option>
                  <option value="75">75%</option>
                  <option value="100">100%</option>
                  <option value="125">125%</option>
                  <option value="150">150%</option>
                  <option value="200">200%</option>
                  <option value="300">300%</option>
                </select>
                <button className="pdf-icon-btn" onClick={handleZoomIn} disabled={scale >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1]} title="Zoom In">
                  <FontAwesomeIcon icon={faSearchPlus} />
                </button>
                <button className="pdf-text-btn" onClick={fitToWidth}>Fit to Width</button>
                <button className="pdf-text-btn" onClick={resetZoom}>Actual Size</button>
              </div>
            </div>

            <div className="pdf-document-container" ref={documentContainerRef}>
              <Document
                file={pdfToDisplay.url}
                options={PDF_OPTIONS}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={
                  <div className="pdf-loading">
                    <div className="pdf-loading-spinner"></div>
                    <span>Loading PDF...</span>
                  </div>
                }
                error={
                  <div className="pdf-error">
                    <span>Failed to load PDF file.</span>
                  </div>
                }
              >
                <Page
                  pageNumber={pageNumber}
                  scale={scale}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                  loading={
                    <div className="pdf-loading">
                      <div className="pdf-loading-spinner"></div>
                      <span>Loading page...</span>
                    </div>
                  }
                />
              </Document>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PdfViewer;
